/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  TextInput,
  Checkbox,
  Table,
  Text,
  Title,
  LoadingOverlay,
  Alert,
  Badge,
  Paper,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconSearch,
  IconSquareCheck,
  IconInfoCircle,
  IconCheck,
  IconCirclePlus
} from '@tabler/icons-react';
import { Question } from '../CreateQuestionModal';
import { useQuery } from '@tanstack/react-query';
import { masterGroupService } from 'services/lia/masterGroupService';
import { masterQuestionService } from 'services/lia/masterQuestionService';
import { useDebouncedValue } from '@mantine/hooks';
import DataTables from 'components/DataTables';

interface SelectGroupQuestionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_questions: Question[]) => void;
  moduleId: string;
  moduleName: string;
  respondentTypes: string[];
  externalTypeIds?: string[];
  externalTypeNames?: string[];
}

const SelectGroupQuestionModal: React.FC<SelectGroupQuestionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  moduleId,
  moduleName,
  respondentTypes,
  externalTypeIds = [],
  externalTypeNames = []
}) => {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);

  // Group search and pagination states
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupPage, setGroupPage] = useState(1);
  const [debouncedGroupSearch] = useDebouncedValue(groupSearchQuery, 300);
  const [selectedGroupName, setSelectedGroupName] = useState('');

  // Fetch groups with search and pagination
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['masterGroups', debouncedGroupSearch, groupPage],
    queryFn: () =>
      masterGroupService.getList({
        search: debouncedGroupSearch,
        page: groupPage,
        limit: 10
      }),
    enabled: opened,
    staleTime: 5 * 60 * 1000
  });

  const { data: questionsData, isLoading: isLoadingQuestions } = useQuery({
    queryKey: ['masterQuestions', selectedGroup, 'modal'],
    queryFn: () =>
      masterQuestionService.getList({
        search: '',
        page: 1,
        limit: 100,
        groups: selectedGroup ? [selectedGroup] : []
      }),
    enabled: !!selectedGroup,
    staleTime: 5 * 60 * 1000
  });

  useEffect(() => {
    if (!opened) {
      setSelectedGroup(null);
      setSearchQuery('');
      setSelectedQuestions([]);
      setGroupSearchQuery('');
      setGroupPage(1);
      setSelectedGroupName('');
    }
  }, [opened]);

  // Create columns for the groups DataTable
  const groupColumns = [
    {
      heading: 'Group Name',
      props: 'title',
      sortable: true,
      active: true,
      disabled: false,
      id: 'title'
    },
    {
      heading: 'Description',
      props: 'desc',
      sortable: false,
      active: true,
      disabled: false,
      id: 'desc'
    },
    {
      heading: 'Actions',
      props: 'actions',
      sortable: false,
      active: true,
      disabled: false,
      id: 'actions'
    }
  ];

  // Handle group selection
  const handleGroupSelect = (group: any) => {
    setSelectedGroup(group.msGroupQuestionID);
    setSelectedGroupName(group.title);
  };

  // Handle pagination changes
  const handleGroupPageChange = (page: number) => {
    setGroupPage(page);
  };

  // Prepare group data for DataTables with action buttons
  const groupTableData =
    groupsData?.data?.map((group: any) => ({
      ...group,
      actions: (
        <Tooltip
          label={selectedGroup === group.msGroupQuestionID ? 'Selected' : 'Select this group'}
          position='top'
          withArrow
        >
          <ActionIcon
            size='lg'
            variant={selectedGroup === group.msGroupQuestionID ? 'filled' : 'outline'}
            color={selectedGroup === group.msGroupQuestionID ? 'green' : '#7B2183'}
            onClick={() => handleGroupSelect(group)}
            disabled={selectedGroup === group.msGroupQuestionID}
          >
            {selectedGroup === group.msGroupQuestionID ? (
              <IconCheck size={16} />
            ) : (
              <IconCirclePlus size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      )
    })) || [];

  // Filter questions based on search query (client-side filtering)
  const filteredQuestions = React.useMemo(() => {
    if (!questionsData?.data) return [];

    let questions = questionsData.data;

    // Apply search filter if needed
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      questions = questions.filter(
        (question: any) =>
          question.title.toLowerCase().includes(query) ||
          (question.description && question.description.toLowerCase().includes(query)) ||
          (question.category && question.category.toLowerCase().includes(query))
      );
    }

    // Sort questions by sequence and dateIn to maintain consistent order (ascending)
    return [...questions].sort((a: any, b: any) => {
      const seqA = a.sequence || 0;
      const seqB = b.sequence || 0;

      // Primary sort by sequence (ascending - lowest sequence first)
      if (seqA !== seqB) {
        return seqA - seqB;
      }

      // Secondary sort by dateIn (ascending - oldest first) if sequences are equal
      const dateA = new Date(a.dateIn || 0).getTime();
      const dateB = new Date(b.dateIn || 0).getTime();
      return dateA - dateB;
    });
  }, [questionsData, searchQuery]);

  const handleSelectAll = () => {
    if (selectedQuestions.length === filteredQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filteredQuestions.map((q: any) => q.msQuestionID));
    }
  };

  const handleToggleQuestion = (questionId: string) => {
    setSelectedQuestions((prev) => {
      if (prev.includes(questionId)) {
        return prev.filter((id) => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  const handleSubmit = () => {
    const selectedQuestionObjects: Question[] = [];
    const processedQuestions = new Set<string>(); // Track processed questions

    const selectedQuestionData = filteredQuestions.filter((q: any) =>
      selectedQuestions.includes(q.msQuestionID)
    );

    // Since filteredQuestions is already sorted, we don't need to sort again
    selectedQuestionData.forEach((q: any) => {
      // Only process each question once
      if (!processedQuestions.has(q.msQuestionID)) {
        processedQuestions.add(q.msQuestionID);

        selectedQuestionObjects.push({
          id: q.msQuestionID,
          title: q.title,
          description: q.description || '',
          category: q.category || '',
          skalaCategory: q.skalaCategory || '',
          type: q.type,
          moduleId: moduleId,
          respondentType: respondentTypes.join(','), // Combine all respondent types
          groupId: q.msGroupQuestionID,
          sequence: q.sequence || 0, // Preserve original sequence from database
          rangeItems: Array.isArray(q.rangeItems)
            ? q.rangeItems.map((item: any, itemIndex: number) => ({
                label: item.label,
                value: item.value,
                sequence: item.sequence || itemIndex + 1
              }))
            : Array.isArray(q.rangeItem)
              ? q.rangeItem.map((item: any, itemIndex: number) => ({
                  label: item.label,
                  value: item.value,
                  sequence: item.sequence || itemIndex + 1
                }))
              : [],
          moduleName:
            groupsData?.data?.find((g: any) => g.msGroupQuestionID === q.msGroupQuestionID)
              ?.title || 'Unknown Group',
          // Add external type information if applicable
          ...(respondentTypes.includes('External') &&
            externalTypeIds.length > 0 && {
              externalTypeId: externalTypeIds.join(','), // Combine all external type IDs
              externalTypeName: externalTypeNames.join(',') // Combine all external type names
            })
        });
      }
    });

    onSubmit(selectedQuestionObjects);
    onClose();
  };

  // Update the calculation to show actual unique questions
  const totalQuestionsToCreate = selectedQuestions.length;

  return (
    <Modal opened={opened} onClose={onClose} withCloseButton={false} size='xl' centered>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoadingGroups} overlayProps={{ radius: 'sm', blur: 2 }} />

        <Stack gap='md' p={16}>
          <Title order={3}>Choose From Group Question</Title>

          <Alert icon={<IconInfoCircle size={16} />} title='Selection Information' color='blue'>
            Selecting questions for <b>{respondentTypes.join(', ')}</b> assessment in module{' '}
            <b>{moduleName}</b>
            {respondentTypes.includes('External') && externalTypeNames.length > 0 && (
              <>
                <br />
                <br />
                <Text size='sm'>External assessment types:</Text>
                <Group mt={5}>
                  {externalTypeNames.map((typeName, idx) => (
                    <Badge key={idx} color='blue' variant='light' size='sm'>
                      {typeName}
                    </Badge>
                  ))}
                </Group>
              </>
            )}
            {selectedQuestions.length > 0 && (
              <>
                <br />
                <Text size='sm' fw={500}>
                  {selectedQuestions.length} question(s) selected × {respondentTypes.length}{' '}
                  respondent type(s) = {totalQuestionsToCreate} total questions will be created
                </Text>
              </>
            )}
          </Alert>

          <Stack gap='sm'>
            <Text fw={500} size='sm'>
              Select Question Group
            </Text>

            <TextInput
              placeholder='Search groups...'
              value={groupSearchQuery}
              onChange={(e) => {
                setGroupSearchQuery(e.currentTarget.value);
                setGroupPage(1); // Reset to first page when searching
              }}
              rightSection={<IconSearch size={18} />}
              disabled={isLoadingGroups}
              size='md'
              styles={{
                input: {
                  fontSize: '14px',
                  padding: '12px 16px'
                }
              }}
            />

            <DataTables
              columns={groupColumns}
              data={groupTableData}
              loading={isLoadingGroups}
              totalData={groupsData?.totalRecords}
              currentPage={groupPage}
              currentSize={10}
              handleChangePage={handleGroupPageChange}
              emptyMessageTitle='No Groups Found'
              emptyMessageDescription={
                groupSearchQuery
                  ? `No groups found matching "${groupSearchQuery}"`
                  : 'No groups available'
              }
            />
          </Stack>

          {selectedGroup && (
            <>
              <TextInput
                placeholder='Search questions by title, description, or category...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                rightSection={<IconSearch size={16} />}
                disabled={isLoadingQuestions}
              />

              <div style={{ position: 'relative', minHeight: '200px' }}>
                <LoadingOverlay
                  visible={isLoadingQuestions}
                  overlayProps={{ radius: 'sm', blur: 2 }}
                />

                {!isLoadingQuestions && (
                  <>
                    {filteredQuestions.length > 0 ? (
                      <>
                        <Group justify='space-between' mb='md'>
                          <Group gap='xs'>
                            <Text size='sm' fw={500}>
                              {filteredQuestions.length} question(s) found
                            </Text>
                            {searchQuery && (
                              <Badge size='sm' color='blue' variant='light'>
                                Filtered
                              </Badge>
                            )}
                          </Group>
                          <Button
                            variant='subtle'
                            leftSection={<IconSquareCheck size={16} />}
                            onClick={handleSelectAll}
                            color='#7B2183'
                            size='sm'
                            disabled={filteredQuestions.length === 0}
                          >
                            {selectedQuestions.length === filteredQuestions.length
                              ? 'Deselect All'
                              : 'Select All'}
                          </Button>
                        </Group>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <Table striped border={1}>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th style={{ width: 50 }}>No.</Table.Th>
                                <Table.Th>Questions</Table.Th>
                                <Table.Th>Category</Table.Th>
                                <Table.Th>Skala Category</Table.Th>
                                <Table.Th style={{ width: 120 }}>Type</Table.Th>
                                <Table.Th style={{ width: 80, textAlign: 'center' }}>
                                  Select
                                </Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {filteredQuestions.map((question: any, index: number) => (
                                <Table.Tr key={question.msQuestionID}>
                                  <Table.Td>{index + 1}</Table.Td>
                                  <Table.Td>
                                    <Stack gap={4}>
                                      <Text size='sm' fw={500} lineClamp={2}>
                                        {question.title}
                                      </Text>
                                      {question.description && (
                                        <Text size='xs' c='dimmed' lineClamp={2}>
                                          {question.description}
                                        </Text>
                                      )}
                                    </Stack>
                                  </Table.Td>
                                  <Table.Td>
                                    {question.category ? (
                                      <Group gap={4} wrap='wrap'>
                                        {question.category
                                          .split(',')
                                          .map((cat: string, idx: number) => (
                                            <Badge
                                              key={idx}
                                              size='xs'
                                              color='grape'
                                              variant='light'
                                            >
                                              {cat.trim()}
                                            </Badge>
                                          ))}
                                      </Group>
                                    ) : (
                                      <Text size='xs' c='dimmed'>
                                        No Category
                                      </Text>
                                    )}
                                  </Table.Td>
                                  <Table.Td>
                                    {question.skalaCategory ? (
                                      <Badge size='xs' color='indigo' variant='light'>
                                        {question.skalaCategory}
                                      </Badge>
                                    ) : (
                                      <Text size='xs' c='dimmed'>
                                        No Skala Category
                                      </Text>
                                    )}
                                  </Table.Td>
                                  <Table.Td>
                                    <Badge size='xs' color='blue' variant='filled'>
                                      {question.type}
                                    </Badge>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: 'center' }}>
                                    <Checkbox
                                      checked={selectedQuestions.includes(question.msQuestionID)}
                                      onChange={() => handleToggleQuestion(question.msQuestionID)}
                                      color='#7B2183'
                                    />
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <Stack align='center' py='xl'>
                        <Text c='dimmed' ta='center'>
                          {searchQuery
                            ? `No questions found matching "${searchQuery}"`
                            : 'No questions found in this group'}
                        </Text>
                        {searchQuery && (
                          <Button variant='subtle' size='sm' onClick={() => setSearchQuery('')}>
                            Clear search
                          </Button>
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          <Group justify='space-between' mt='md'>
            <Group gap='xs'>
              {selectedQuestions.length > 0 && (
                <Text size='sm' c='dimmed'>
                  {totalQuestionsToCreate} total questions will be created
                </Text>
              )}
            </Group>

            <Group gap='sm'>
              <Button variant='outline' onClick={onClose} color='#7B2183'>
                Cancel
              </Button>
              <Button
                color='#7B2183'
                onClick={handleSubmit}
                disabled={selectedQuestions.length === 0}
              >
                Add {totalQuestionsToCreate} Question{totalQuestionsToCreate !== 1 ? 's' : ''}
              </Button>
            </Group>
          </Group>
        </Stack>
      </div>
    </Modal>
  );
};

export default SelectGroupQuestionModal;
